import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import Sidebar from './components/Sidebar';
import DashboardPage from './pages/DashboardPage';
import EventsPage from './pages/EventsPage';
import TemplatesPage from './pages/TemplatesPage';
import MapPage from './pages/MapPage';
import HistoryPage from './pages/HistoryPage';
import AssetsPage from './pages/AssetsPage';
import ActionPage from './pages/ActionPage';
import WorkflowPage from './pages/WorkflowPage';
import CreateEditWorkflowPage from './components/Workflow/CreateWorkflowPage';
import IncidentPage from './pages/IncidentPage';
import IncidentDetailPage from './components/Incident/IncidentDetailPage';
import AssetDetailPage from './components/Assets/AssetDetail';
import CrashDataTable from './components/CrashData/CrashDataTable';
import CrashDetail from './components/CrashData/CrashDetail';
import ViewWorkflowPage from './components/Workflow/ViewWorkflowPage';
import UserList from './components/User/UserList';
import AddOrEditUser from './components/User/AddOrEditUser';
import OrganizationList from './components/Organization/OrganizationList';
import AddOrganization from './components/Organization/AddOrganization';
import CreateEntity from './components/Entities/CreateEntity';
import EntityList from './components/Entities/List';
import Unauthorized from './Unauthorized';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RoleList from './components/Role/RoleList';
import AddRole from './components/Role/AddRole';
import { verifyToken } from './api/api';
import AppPage from './pages/AppPage';
import CreateNewApp from './components/App/CreateNewApp';
import { GlobalProvider } from './GlobalProvider';

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  
  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        navigate('/login');
        return;
      }

      try {
        const response = await verifyToken(token);
        if (response.data.valid) {
          setIsAuthenticated(true);
          setAuth(response.data.decoded);
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          navigate('/login');
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };

    checkAuthentication();
  }, [location]);

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {isAuthenticated ? (
        <>
          {/* Sidebar */}
          <div
            style={{
              position: 'fixed',
              width: '200px', // Adjust the width of the sidebar
              height: '100%',
              top: 0,
              left: 0,
              zIndex: 1000, // Ensure it stays on top
            }}
          >
            <Sidebar />
          </div>

          {/* Main Content */}
          <div
            style={{
              flex: 1,
              marginLeft: '280px', // Matches the sidebar width
              padding: '16px',
              overflowY: 'auto',
            }}
          >
            <Routes>
              <Route path="/" element={ auth.currentApp.adminApp ? <AppPage/>: <DashboardPage />} />
              <Route path="/dashboard"  element={ auth.currentApp.adminApp ? <AppPage/>: <DashboardPage />} />
              <Route path="/incidents" element={<IncidentPage />} />
              <Route path="/incident/:id" element={<IncidentDetailPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/create-entity" element={<CreateEntity />} />
              <Route path="/entities" element={<EntityList />} />
             
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/actions" element={<ActionPage />} />
              <Route path="/apps" element={<AppPage />} />
              <Route path="/app-create" element={<CreateNewApp />} />
              <Route path="/app/:appId/edit" element={<CreateNewApp />} />
              <Route path="/workflow" element={<WorkflowPage />} />
              <Route path="/create-workflow" element={<CreateEditWorkflowPage />} />
              <Route path="/edit-workflow/:workflowId" element={<CreateEditWorkflowPage />} />
              <Route path="/crash-data" element={<CrashDataTable />} />
              <Route path="/crash-data/:id" element={<CrashDetail />} />
              <Route path="/asset/:type/:assetId" element={<AssetDetailPage />} />
              <Route path="/workflow/:id" element={<ViewWorkflowPage />} />
              <Route
                path="/users"
                element={
                  <ProtectedRoute roles={['org_admin', 'system_admin']}>
                    <UserList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-user"
                element={
                  <ProtectedRoute roles={['org_admin', 'system_admin']}>
                    <AddOrEditUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-user/:userId"
                element={
                  <ProtectedRoute roles={['org_admin', 'system_admin']}>
                    <AddOrEditUser />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute roles={['system_admin']}>
                    <OrganizationList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-organization"
                element={
                  <ProtectedRoute roles={['system_admin']}>
                    <AddOrganization />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles"
                element={
                  <ProtectedRoute roles={['system_admin']}>
                    <RoleList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-role"
                element={
                  <ProtectedRoute roles={['system_admin']}>
                    <AddRole />
                  </ProtectedRoute>
                }
              />
              <Route path="/unauthorized" element={<Unauthorized />} />
            </Routes>
          </div>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          width="100%"
          bgcolor="#f4f4f4"
        >
          <Login />
        </Box>
      )}
    </div>
  );
};

const AppWrapper = () => (
  <Router>
    <CssBaseline />
    <GlobalProvider>
      <App />
    </GlobalProvider>
  </Router>
);

export default AppWrapper;
